/**
 * Upload a workbook into the draft (T3.10).
 *
 * The upload REPLACES the draft's content wholesale and does not publish
 * (design.md § 6), so the dialogue says so before the file is sent. The size
 * and extension checks here are a courtesy that saves a round trip; the API
 * sniffs the content and is the only real enforcement.
 *
 * Progress comes from XMLHttpRequest, because `fetch` cannot report upload
 * progress. When the API accepts the file it returns the import's warnings
 * (weightings that do not sum to 100%, unrecognised states, a missing
 * Schedule sheet), which are shown here as a list the editor can read before
 * closing. They are the API's own strings; this dialogue does not reword them.
 *
 * Drag and drop. Like the generator, the whole page is a drop target, but a drop
 * here only CHOOSES the file: it opens this dialogue with the workbook already
 * selected (or swaps the selection if the dialogue is open), and nothing is sent
 * until Upload is pressed. An upload replaces the draft, which must not happen
 * by a slip of the hand, and the dialogue is where the warning and the progress
 * live. The component is only mounted for people who may edit, so a viewer has
 * no listeners and no affordance. The file button stays as prominent as the
 * drop zone, because a keyboard or screen-reader user cannot drop.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, uploadWithProgress } from '../../lib/api';
import { blueprintKeys } from '../../lib/blueprints';
import { useTerms } from '../../lib/terms';
import { Dialog } from './Dialog';
import { TemplateButton } from './TemplateButton';
// The dialogue also opens from the empty-blueprint screen, which does not mount
// BlueprintView, so it imports the stylesheet itself rather than relying on it.
import '../../styles/progress.css';

const MAX_BYTES = 10 * 1024 * 1024;

function contractErrors(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const details = err.body?.error?.details;
  const list = (details as { errors?: unknown } | undefined)?.errors;
  return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface UploadResponse {
  warnings?: string[];
}

type Phase =
  | { kind: 'choose' }
  | { kind: 'sending'; fraction: number }
  | { kind: 'done'; warnings: string[] };

export function UploadDialog({
  blueprintId,
  replacesContent,
  pendingWrites,
}: {
  blueprintId: string;
  /** False when there is no draft yet, so nothing is being replaced. */
  replacesContent: boolean;
  pendingWrites: number;
}) {
  const terms = useTerms();
  const queryClient = useQueryClient();
  const abort = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'choose' });
  const [error, setError] = useState<string | null>(null);
  // Workbook contract errors arrive as a list (`error.details.errors`, at most 50,
  // each naming its sheet and row). They are the API's own strings.
  const [problems, setProblems] = useState<string[]>([]);

  const sending = phase.kind === 'sending';

  // Is a file being dragged over the page, and would a drop be taken?
  const [dragging, setDragging] = useState(false);
  const accepting = phase.kind === 'choose' && pendingWrites === 0;

  const reset = () => {
    setFile(null);
    setPhase({ kind: 'choose' });
    setError(null);
    setProblems([]);
  };

  const close = () => {
    if (sending) return;
    const imported = phase.kind === 'done';
    setOpen(false);
    reset();
    // Refresh only once the dialogue is closed. The draft's content has been
    // replaced wholesale, so every cached model for this blueprint is stale —
    // but refreshing earlier can swap the screen underneath this dialogue (an
    // empty blueprint becomes a populated one) and unmount it before the
    // warnings have been read.
    if (imported) void queryClient.invalidateQueries({ queryKey: blueprintKeys.all });
  };

  const choose = (picked: File | null) => {
    setError(null);
    setProblems([]);
    if (!picked) {
      setFile(null);
      return;
    }
    if (!/\.xlsx$/i.test(picked.name)) {
      setFile(null);
      setError('Choose an Excel workbook with the .xlsx extension.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setError('That file is larger than the 10 MB limit.');
      return;
    }
    setFile(picked);
  };

  // The latest `choose`, so the page-level listeners (registered once per
  // `accepting` change) never act on a stale closure.
  const chooseRef = useRef(choose);
  chooseRef.current = choose;

  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setDragging(true);
    };
    const over = (e: DragEvent) => {
      if (!hasFiles(e) || !e.dataTransfer) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = accepting ? 'copy' : 'none';
    };
    const leave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      // Always cancel the default, or the browser navigates away to the file.
      e.preventDefault();
      depth = 0;
      setDragging(false);
      if (!accepting) return;
      const files = e.dataTransfer?.files;
      setOpen(true);
      if (files && files.length > 1) {
        setFile(null);
        setProblems([]);
        setError('Drop one workbook at a time.');
      } else {
        chooseRef.current(files?.[0] ?? null);
      }
    };

    document.addEventListener('dragenter', enter);
    document.addEventListener('dragover', over);
    document.addEventListener('dragleave', leave);
    document.addEventListener('drop', drop);
    return () => {
      document.removeEventListener('dragenter', enter);
      document.removeEventListener('dragover', over);
      document.removeEventListener('dragleave', leave);
      document.removeEventListener('drop', drop);
      setDragging(false);
    };
  }, [accepting]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || sending) return;
    setError(null);
    setProblems([]);
    setPhase({ kind: 'sending', fraction: 0 });

    const body = new FormData();
    body.append('file', file);
    abort.current = new AbortController();

    try {
      const result = await uploadWithProgress<UploadResponse | undefined>(
        `/blueprints/${blueprintId}/upload`,
        body,
        (fraction) => setPhase({ kind: 'sending', fraction }),
        abort.current.signal,
      );
      setPhase({ kind: 'done', warnings: result?.warnings ?? [] });
    } catch (err) {
      setPhase({ kind: 'choose' });
      setProblems(contractErrors(err));
      setError(
        err instanceof ApiError
          ? err.message
          : 'The upload did not complete. Nothing was changed.',
      );
    } finally {
      abort.current = null;
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        disabled={pendingWrites > 0}
        title={pendingWrites > 0 ? 'Waiting for your latest changes to finish saving' : undefined}
      >
        Upload Excel
      </button>

      {dragging && !open ? (
        <div className="bp-dropveil" aria-hidden="true">
          {accepting ? (
            <>
              <b>Drop the workbook to choose it</b>
              <span>
                Nothing is uploaded yet. You will confirm first
                {replacesContent ? ` — it replaces the draft of this ${terms.noun('blueprint')}` : ''}.
              </span>
            </>
          ) : (
            <b>Wait for your latest changes to finish saving, then drop again</b>
          )}
        </div>
      ) : null}

      <Dialog open={open} title="Upload a workbook" onClose={close} dismissible={!sending}>
        {phase.kind === 'done' ? (
          <div className="bp-dialog-form">
            <p role="status">The workbook was imported into the draft. It has not been published.</p>
            {phase.warnings.length ? (
              <div className="warnbar show">
                <b>{phase.warnings.length} thing(s) to check:</b>
                <ul>
                  {phase.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="bp-dialog-actions">
              <button type="button" className="btn primary" onClick={close}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="bp-dialog-form">
            <p>
              {replacesContent
                ? `This replaces everything in the current draft of this ${terms.noun('blueprint')}, including any changes you have made to it. `
                : ''}
              It does not publish: the Live version stays as it is until you publish.
            </p>

            {/* The ported theme.css hides every file input, as the generator drives
                its own through a button. So does this: the button is the control,
                the input stays hidden and is opened from it. */}
            <div className="field">
              <span className="field-label">Workbook (.xlsx, up to 10 MB)</span>
              <div className={`bp-dropzone${dragging && accepting ? ' dragging' : ''}`}>
                <div className="bp-file-row">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => fileInput.current?.click()}
                    disabled={sending}
                  >
                    {file ? 'Choose a different file' : 'Choose file'}
                  </button>
                  <span className="bp-file-name" title={file?.name}>
                    {file ? `${file.name} · ${formatSize(file.size)}` : 'No file chosen'}
                  </span>
                </div>
                <p className="bp-dropzone-hint">
                  {dragging && accepting
                    ? 'Release to choose this file. It is not uploaded until you press Upload.'
                    : 'Or drag a workbook onto this window.'}
                </p>
              </div>
              {/* T7.41. Beside the drop zone, and worded so it cannot be read as
                  an export of this blueprint — "Download Excel" in the bar
                  behind this dialogue is that, and the two sit close together. */}
              <div className="bp-template-row">
                <TemplateButton />
                <span className="bp-dropzone-hint">
                  Starting a new plan? The blank template has every sheet and column with one
                  example row to follow. It is not an export of this {terms.noun('blueprint').toLowerCase()}.
                </span>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  choose(e.target.files?.[0] ?? null);
                  // Allow choosing the same file again after a rejected attempt.
                  e.target.value = '';
                }}
              />
            </div>

            {sending ? (
              <progress
                value={phase.fraction}
                max={1}
                aria-label="Upload progress"
                className="bp-progress"
              />
            ) : null}

            {error ? (
              <div role="alert">
                <p className="bp-inline-err">{error}</p>
                {problems.length ? (
                  <ul className="bp-problems">
                    {problems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div className="bp-dialog-actions">
              <button type="button" className="btn" onClick={close} disabled={sending}>
                Cancel
              </button>
              <button type="submit" className="btn primary" disabled={!file || sending}>
                {sending ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </form>
        )}
      </Dialog>
    </>
  );
}
